/**
 * Cash Flow report — operating / investing / financing buckets, plus
 * opening / closing cash and the net change.
 *
 * Data is derived from real entity data via usePersistentMockState:
 *   - accounts       → identify cash accounts (bank, petty cash)
 *   - journalEntries → filter posted entries, classify by sourceTable
 *   - journalLines   → compute cash deltas per entry
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDownRight, ArrowUpRight, Wallet } from "lucide-react";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import { KpiCard, cn } from "@bizop/ui-kit";

import { PageHeader } from "../../../../components/PageHeader";
import { usePersistentMockState } from "../../../../lib/usePersistentMockState";
import {
  mockAccounts,
  mockJournalEntries,
  mockJournalLines,
  type MockAccount,
  type MockJournalEntry,
  type MockJournalLine,
} from "../../../../lib/glMock";
import {
  PeriodSelector,
  defaultPeriodWindow,
  type PeriodWindow,
} from "../../../../components/gl/PeriodSelector";
import { ExportButton } from "../../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../../lib/export";

export const Route = createFileRoute("/_app/finance/reports/cash-flow")({
  component: CashFlowPage,
});

type CashFlowItem = {
  entryId: string;
  entryNumber: string;
  description: string;
  entryDate: number;
  sourceTable: string | null;
  amountInBaseCents: bigint;
};

/* ── Pure derive function ─────────────────────────────────────── */

function buildCashFlowReport(
  fromDate: number,
  toDate: number,
  accounts: MockAccount[],
  entries: MockJournalEntry[],
  journalLines: MockJournalLine[],
): {
  operating: { items: CashFlowItem[]; totalInBaseCents: bigint };
  investing: { items: CashFlowItem[]; totalInBaseCents: bigint };
  financing: { items: CashFlowItem[]; totalInBaseCents: bigint };
  netChangeInBaseCents: bigint;
  openingCashInBaseCents: bigint;
  closingCashInBaseCents: bigint;
} {
  // Identify cash accounts by code
  const accountMap = new Map<string, MockAccount>();
  const cashIds = new Set<string>();
  for (const acc of accounts) {
    accountMap.set(acc._id, acc);
    if (acc.code === "1000" || acc.code === "1100") {
      cashIds.add(acc._id);
    }
  }

  // Build entry lookup
  const entryMap = new Map<string, MockJournalEntry>();
  for (const e of entries) {
    entryMap.set(e._id, e);
  }

  // Group lines by entry id
  const linesByEntry = new Map<string, MockJournalLine[]>();
  for (const l of journalLines) {
    const list = linesByEntry.get(l.journalEntryId) ?? [];
    list.push(l);
    linesByEntry.set(l.journalEntryId, list);
  }

  let opening = 0n;
  let closing = 0n;
  const operating: CashFlowItem[] = [];
  const investing: CashFlowItem[] = [];
  const financing: CashFlowItem[] = [];

  for (const [entryId, lines] of linesByEntry.entries()) {
    const entry = entryMap.get(entryId);
    if (!entry || entry.status !== "posted") continue;

    let cashDelta = 0n;
    let touchesFixedAsset = false;
    for (const l of lines) {
      if (cashIds.has(l.accountId)) {
        cashDelta += l.debitInBaseCents - l.creditInBaseCents;
      } else {
        const acc = accountMap.get(l.accountId);
        if (acc && acc.code.startsWith("15")) touchesFixedAsset = true;
      }
    }
    if (cashDelta === 0n) continue;

    if (entry.entryDate < fromDate) {
      opening += cashDelta;
      closing += cashDelta;
      continue;
    }
    if (entry.entryDate > toDate) continue;
    closing += cashDelta;

    const item: CashFlowItem = {
      entryId,
      entryNumber: entry.entryNumber,
      description: entry.description,
      entryDate: entry.entryDate,
      sourceTable: entry.sourceTable,
      amountInBaseCents: cashDelta,
    };
    if (touchesFixedAsset) {
      investing.push(item);
    } else if (
      entry.sourceTable === "loans" ||
      entry.sourceTable === "loanRepayments" ||
      entry.description.toLowerCase().includes("loan")
    ) {
      financing.push(item);
    } else {
      operating.push(item);
    }
  }

  const sum = (xs: CashFlowItem[]) =>
    xs.reduce((s, i) => s + i.amountInBaseCents, 0n);
  const operatingTotal = sum(operating);
  const investingTotal = sum(investing);
  const financingTotal = sum(financing);

  return {
    operating: { items: operating, totalInBaseCents: operatingTotal },
    investing: { items: investing, totalInBaseCents: investingTotal },
    financing: { items: financing, totalInBaseCents: financingTotal },
    netChangeInBaseCents: operatingTotal + investingTotal + financingTotal,
    openingCashInBaseCents: opening,
    closingCashInBaseCents: closing,
  };
}

/* ── Component ────────────────────────────────────────────────── */

function CashFlowPage() {
  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow());

  const [accounts] = usePersistentMockState<MockAccount[]>("accounts", mockAccounts);
  const [entries] = usePersistentMockState<MockJournalEntry[]>("journalEntries", mockJournalEntries);
  const [journalLines] = usePersistentMockState<MockJournalLine[]>(
    "journalLines",
    mockJournalLines,
  );

  const report = useMemo(
    () => buildCashFlowReport(period.fromDate, period.toDate, accounts, entries, journalLines),
    [period.fromDate, period.toDate, accounts, entries, journalLines],
  );
  const printRef = useRef<HTMLDivElement>(null);

  function handleCsv(extension: "csv" | "xls") {
    type CfRow = {
      activity: string;
      date: string;
      account: string;
      inflowCents: bigint;
      outflowCents: bigint;
      netCents: bigint;
      currency: string;
    };
    const rows: CfRow[] = [];
    function pushBucket(label: string, items: CashFlowItem[], total: bigint) {
      for (const item of items) {
        const inflow = item.amountInBaseCents > 0n ? item.amountInBaseCents : 0n;
        const outflow = item.amountInBaseCents < 0n ? -item.amountInBaseCents : 0n;
        rows.push({
          activity: label,
          date: formatDateForCsv(item.entryDate),
          account: `${item.entryNumber} · ${item.description}`,
          inflowCents: inflow,
          outflowCents: outflow,
          netCents: item.amountInBaseCents,
          currency: "BDT",
        });
      }
      rows.push({
        activity: label,
        date: "",
        account: `Total ${label}`,
        inflowCents: total > 0n ? total : 0n,
        outflowCents: total < 0n ? -total : 0n,
        netCents: total,
        currency: "BDT",
      });
    }
    pushBucket(
      "Operating",
      report.operating.items,
      report.operating.totalInBaseCents,
    );
    pushBucket(
      "Investing",
      report.investing.items,
      report.investing.totalInBaseCents,
    );
    pushBucket(
      "Financing",
      report.financing.items,
      report.financing.totalInBaseCents,
    );
    rows.push({
      activity: "Summary",
      date: "",
      account: "Opening cash",
      inflowCents: 0n,
      outflowCents: 0n,
      netCents: report.openingCashInBaseCents,
      currency: "BDT",
    });
    rows.push({
      activity: "Summary",
      date: "",
      account: "Net change",
      inflowCents: 0n,
      outflowCents: 0n,
      netCents: report.netChangeInBaseCents,
      currency: "BDT",
    });
    rows.push({
      activity: "Summary",
      date: "",
      account: "Closing cash",
      inflowCents: 0n,
      outflowCents: 0n,
      netCents: report.closingCashInBaseCents,
      currency: "BDT",
    });

    const columns: ExportColumn<CfRow>[] = [
      { key: "activity", label: "Activity" },
      { key: "date", label: "Date" },
      { key: "account", label: "Account / Entry" },
      { key: "inflowCents", label: "Inflow" },
      { key: "outflowCents", label: "Outflow" },
      { key: "netCents", label: "Net" },
      { key: "currency", label: "Currency" },
    ];
    downloadCsv(exportFilename("cash-flow", extension), toCsv(rows, columns));
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Cash Flow Statement",
      subtitle: `${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}`,
    });
  }

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Cash Flow"
        description={`Cash movement ${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}.`}
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      <PeriodSelector value={period} onChange={setPeriod} />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Cash flow KPIs"
      >
        <KpiCard
          caption="Opening cash"
          value={formatMoneySmart(report.openingCashInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint="Bank + petty"
        />
        <KpiCard
          caption="Net change"
          value={formatMoneySmart(report.netChangeInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={
            report.netChangeInBaseCents >= 0n
              ? "Cash inflow"
              : "Cash outflow"
          }
        />
        <KpiCard
          caption="Closing cash"
          value={formatMoneySmart(report.closingCashInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint="As of toDate"
        />
        <KpiCard
          caption="Operating share"
          value={formatMoneySmart(report.operating.totalInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={`${report.operating.items.length} entries`}
        />
      </section>

      <Bucket
        title="Operating activities"
        items={report.operating.items}
        total={report.operating.totalInBaseCents}
      />
      <Bucket
        title="Investing activities"
        items={report.investing.items}
        total={report.investing.totalInBaseCents}
      />
      <Bucket
        title="Financing activities"
        items={report.financing.items}
        total={report.financing.totalInBaseCents}
      />

      <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3 text-[13px] flex items-center justify-between font-mono tabular-nums">
        <span className="text-(--text-secondary)">
          Opening{" "}
          {formatMoney(report.openingCashInBaseCents, "BDT", { locale: "en-BD" })} +
          Net{" "}
          {formatMoney(report.netChangeInBaseCents, "BDT", { locale: "en-BD" })} =
          Closing{" "}
        </span>
        <span className="font-semibold text-(--text-primary)">
          {formatMoney(report.closingCashInBaseCents, "BDT", { locale: "en-BD" })}
        </span>
      </div>
    </div>
  );
}

function Bucket({
  title,
  items,
  total,
}: {
  title: string;
  items: CashFlowItem[];
  total: bigint;
}) {
  return (
    <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) overflow-hidden">
      <div className="px-3 py-2 border-b border-(--border-subtle) flex items-center justify-between">
        <span className="font-semibold text-[13px] text-(--text-primary)">
          {title}
        </span>
        <span
          className={cn(
            "font-mono tabular-nums text-[13px]",
            total >= 0n ? "text-(--success)" : "text-(--danger)",
          )}
        >
          {formatMoney(total, "BDT", { locale: "en-BD" })}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="px-3 py-6 text-center text-[12px] text-(--text-tertiary)">
          <Wallet className="inline-block mr-1 h-4 w-4" /> No entries in this bucket.
        </div>
      ) : (
        <table className="w-full text-[13px]">
          <thead className="text-(--text-tertiary) text-[11px] uppercase tracking-wider bg-(--bg-surface)">
            <tr>
              <th className="px-3 py-1.5 text-left">Date</th>
              <th className="px-3 py-1.5 text-left">JE #</th>
              <th className="px-3 py-1.5 text-left">Description</th>
              <th className="px-3 py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items
              .slice()
              .sort((a, b) => a.entryDate - b.entryDate)
              .map((item) => (
                <tr
                  key={item.entryId}
                  className="border-t border-(--border-subtle) hover:bg-(--bg-hover)"
                >
                  <td className="px-3 py-1.5 text-(--text-secondary) tabular-nums">
                    {formatDate(item.entryDate, { style: "short" })}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[12px] text-(--text-primary)">
                    {item.entryNumber}
                  </td>
                  <td className="px-3 py-1.5 text-(--text-primary) line-clamp-1">
                    {item.description}
                  </td>
                  <td
                    className={cn(
                      "px-3 py-1.5 text-right font-mono tabular-nums",
                      item.amountInBaseCents >= 0n
                        ? "text-(--text-primary)"
                        : "text-(--danger)",
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      {item.amountInBaseCents >= 0n ? (
                        <ArrowUpRight className="h-3 w-3" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3" />
                      )}
                      {formatMoney(item.amountInBaseCents, "BDT", {
                        locale: "en-BD",
                      })}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
